import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  interestOptions,
  readAllowedSource,
  workshopSizeOptions,
  type DemoInterest,
  type WorkshopSize,
} from './demo-form';

const API_BASE_URL = '/api/v1';

type PageState = 'form' | 'success';

export default function RequestDemoPage() {
  const { t, i18n } = useTranslation('demo');

  const [pageState, setPageState] = useState<PageState>('form');
  const [contactName, setContactName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [workshopSize, setWorkshopSize] = useState<WorkshopSize | ''>('');
  const [interests, setInterests] = useState<DemoInterest[]>([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const source = useMemo(
    () => readAllowedSource(window.location.search, document.referrer),
    [],
  );

  const locale = i18n.language.startsWith('en') ? 'en' : i18n.language.startsWith('sv') ? 'sv' : 'fi';

  const validate = (): boolean => {
    const next: Record<string, boolean> = {
      contactName: contactName.trim().length === 0,
      company: company.trim().length === 0,
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
      workshopSize: workshopSize === '',
      consent: !consent,
    };
    setFieldErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inFlight.current || submitting) return;
    if (!validate()) {
      setError(t('validationError'));
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    setError(null);

    const honeypot = (event.currentTarget.elements.namedItem('website') as HTMLInputElement | null)
      ?.value;

    const payload = {
      contactName: contactName.trim(),
      company: company.trim(),
      email: email.trim(),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      workshopSize,
      interests,
      locale,
      consent: true as const,
      ...(source ? { source } : {}),
      ...(honeypot ? { website: honeypot } : {}),
    };

    try {
      const response = await fetch(`${API_BASE_URL}/public/demo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        setError(response.status === 400 ? t('validationError') : t('serverError'));
        return;
      }
      setPageState('success');
    } catch {
      setError(typeof navigator !== 'undefined' && !navigator.onLine ? t('offlineError') : t('serverError'));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const toggleInterest = (value: DemoInterest) => {
    setInterests((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 560, width: '100%' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('intro')}
          </Typography>

          {pageState === 'success' ? (
            <Alert severity="success" role="status">
              <Typography fontWeight={600}>{t('successTitle')}</Typography>
              <Typography variant="body2">{t('successBody')}</Typography>
            </Alert>
          ) : (
            <Box component="form" onSubmit={(event) => void handleSubmit(event)} noValidate>
              {error ? (
                <Alert severity="error" sx={{ mb: 2 }} role="alert">
                  {error}
                </Alert>
              ) : null}

              <TextField
                fullWidth
                required
                margin="normal"
                name="contactName"
                label={t('contactName')}
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
                error={Boolean(fieldErrors.contactName)}
                inputProps={{ 'aria-invalid': Boolean(fieldErrors.contactName) }}
              />
              <TextField
                fullWidth
                required
                margin="normal"
                name="company"
                label={t('company')}
                value={company}
                onChange={(event) => setCompany(event.target.value)}
                error={Boolean(fieldErrors.company)}
                inputProps={{ 'aria-invalid': Boolean(fieldErrors.company) }}
              />
              <TextField
                fullWidth
                required
                margin="normal"
                type="email"
                name="email"
                autoComplete="email"
                label={t('email')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={Boolean(fieldErrors.email)}
                inputProps={{ 'aria-invalid': Boolean(fieldErrors.email) }}
              />
              <TextField
                fullWidth
                margin="normal"
                name="phone"
                type="tel"
                autoComplete="tel"
                label={t('phone')}
                helperText={t('phoneHint')}
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
              <TextField
                select
                fullWidth
                required
                margin="normal"
                name="workshopSize"
                label={t('workshopSize')}
                value={workshopSize}
                onChange={(event) => setWorkshopSize(event.target.value as WorkshopSize)}
                error={Boolean(fieldErrors.workshopSize)}
                inputProps={{ 'aria-invalid': Boolean(fieldErrors.workshopSize) }}
              >
                {workshopSizeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuItem>
                ))}
              </TextField>

              <FormControl component="fieldset" variant="standard" sx={{ mt: 2, display: 'block' }}>
                <FormLabel component="legend">{t('interests')}</FormLabel>
                <FormGroup>
                  {interestOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={interests.includes(option.value)}
                          onChange={() => toggleInterest(option.value)}
                          name={option.value}
                        />
                      }
                      label={t(option.labelKey)}
                    />
                  ))}
                </FormGroup>
              </FormControl>

              <Box
                aria-hidden="true"
                sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
              >
                <label htmlFor="demo-website">{t('website')}</label>
                <input id="demo-website" name="website" tabIndex={-1} autoComplete="off" />
              </Box>

              <FormControl error={Boolean(fieldErrors.consent)} sx={{ mt: 2, display: 'block' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      required
                      checked={consent}
                      onChange={(event) => setConsent(event.target.checked)}
                      name="consent"
                    />
                  }
                  label={t('consent')}
                />
                {fieldErrors.consent ? <FormHelperText>{t('consentRequired')}</FormHelperText> : null}
              </FormControl>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                sx={{ mt: 3 }}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {submitting ? t('submitting') : t('submit')}
              </Button>
              {error ? (
                <Button type="submit" fullWidth sx={{ mt: 1 }} disabled={submitting}>
                  {t('retry')}
                </Button>
              ) : null}
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3 }}>
            {t('providerNote')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('privacyNote')}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
